import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, Hourglass, Repeat, TriangleAlert } from "lucide-mui";
import { api } from "../../api/client.ts";
import { keys } from "../../api/queryKeys.ts";
import DurableTasksPanel from "../../components/dashboard/platform/DurableTasksPanel.tsx";
import RecurringJobsPanel from "../../components/dashboard/platform/RecurringJobsPanel.tsx";
import { NEEDS_ATTENTION, seconds } from "../../components/dashboard/platform/recurringFormat.ts";
import KpiTile from "../../components/KpiTile.tsx";
import { useUrlState } from "../../hooks/useUrlState.ts";
import { formatNumber } from "../../utils/format.ts";
import RecurringJobDrawer from "./RecurringJobDrawer.tsx";

/**
 * Recurring work on Restate: every periodic job (its chains, how often and how
 * long it ran, what runs now and next) and the durable-task runner doing the
 * long work their ticks wait on. Ticks and task changes push a refresh.
 */
export default function PlatformRecurringPage() {
  const recurring = useQuery({
    queryKey: keys.platform.recurring(),
    queryFn: api.getPlatformRecurring,
    meta: { live: true },
  });
  const url = useUrlState();
  const openName = url.searchParams.get("job");

  const data = recurring.data;
  if (!data) {
    return (
      <Stack spacing={2}>
        <Grid container spacing={2}>
          {[0, 1, 2, 3].map((i) => (
            <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
              <Skeleton variant="rounded" height={96} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={360} />
      </Stack>
    );
  }

  const jobs = data.jobs;
  const attention = jobs.filter((j) => NEEDS_ATTENTION.includes(j.status));
  const off = jobs.filter((j) => j.status === "off").length;
  const runs = jobs.reduce((sum, j) => sum + j.runs_24h, 0);
  const failed = jobs.reduce((sum, j) => sum + j.failures_24h, 0);
  const queued = data.task_kinds.reduce((sum, k) => sum + k.queued, 0);
  const running = data.task_kinds.reduce((sum, k) => sum + k.running, 0);
  const longestWait = Math.max(0, ...data.task_kinds.map((k) => k.oldest_queued_s ?? 0));

  const index = jobs.findIndex((j) => j.name === openName);
  const open = index >= 0 ? jobs[index] : null;
  const step = (direction: 1 | -1) => {
    if (index < 0 || !jobs.length) return;
    const next = jobs[(index + direction + jobs.length) % jobs.length];
    url.updateParams({ job: next.name }, true);
  };

  return (
    <Stack spacing={2}>
      {!data.live && (
        <Alert severity="info">
          Restate did not answer, so running ticks are not shown and next runs come from each job's last tick
          {data.live_error ? ` (${data.live_error})` : ""}.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiTile
            label="Recurring jobs"
            value={jobs.length}
            icon={<Repeat />}
            caption={off ? `${formatNumber(off)} switched off` : "All switched on"}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiTile
            label="Need attention"
            value={attention.length}
            icon={attention.length ? <TriangleAlert /> : <CircleCheck />}
            color={attention.length ? "warning" : "success"}
            caption={attention.length ? "Late, failing or running long" : "Every job is on time"}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiTile
            label="Runs, last 24h"
            value={runs}
            icon={<Repeat />}
            caption={failed ? `${formatNumber(failed)} failed` : "None failed"}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <KpiTile
            label="Durable tasks running"
            value={running}
            icon={<Hourglass />}
            caption={queued ? `${formatNumber(queued)} waiting, longest ${seconds(longestWait)}` : "None waiting"}
          />
        </Grid>
      </Grid>
      <RecurringJobsPanel jobs={jobs} onOpen={(name) => url.updateParams({ job: name })} />
      <DurableTasksPanel data={data} />
      <RecurringJobDrawer job={open} onClose={() => url.updateParams({ job: null })} onStep={step} />
    </Stack>
  );
}
